import { X } from 'lucide-react';
import React from 'react';
import { STATUS_CONFIG } from '../constants';
import { RecommendationStatus } from '../types';
import { AddButton } from './AddButton';
import { ProfileButton } from './ProfileButton';
import { Wordmark } from './Wordmark';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  photoURL?: string;
  onAddFirst?: () => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, photoURL, onAddFirst }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white z-10">
           <h3 className="font-display font-extrabold text-xl text-brand-dark flex items-baseline gap-1.5">
             <span>Welcome to</span> <Wordmark size="sm" /> <span>🎉</span>
           </h3>
           <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full"><X size={20}/></button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-brand-cream overflow-y-auto max-h-[70vh]">
            <p className="text-brand-dark leading-relaxed">
                Like to keep track of your favorite places? Want to become the top source of a good recommendation amongst your mates? Or just want to eat somewhere good tonight? You're in the right place! 
            </p>

            <div className="space-y-2">
                <h4 className="font-display font-extrabold text-lg text-brand-red">Find Your Friends</h4>
                <p className="text-sm text-brand-dark-light leading-relaxed flex items-center gap-1 flex-wrap">
                    See that face in the top right? <ProfileButton size="sm" className="mx-1" photoURL={photoURL} /> Tap it to find your friends. Following friends means you can see their recommendations, and they can see yours.
                </p>
            </div>

            <div className="space-y-2">
                <h4 className="font-display font-extrabold text-lg text-brand-red">Shout It Out</h4>
                <p className="text-sm text-brand-dark-light leading-relaxed flex items-center gap-1 flex-wrap">
                    Got an opinion on a place you've been? Hit the <AddButton size="sm" className="mx-1" onClick={onAddFirst} /> button. Your map is yours to curate.
                </p>
                
                <div className="mt-4 overflow-hidden rounded-xl border border-brand-cream-dark">
                  <table className="w-full text-left text-sm">
                    <tbody className="divide-y divide-brand-cream-dark bg-white">
                      {[
                        RecommendationStatus.SEAL_OF_APPROVAL,
                        RecommendationStatus.RECOMMENDED,
                        RecommendationStatus.ITS_ALRIGHT,
                        RecommendationStatus.WANT_TO_VISIT,
                        RecommendationStatus.DO_NOT_RECOMMEND,
                      ].map((status, index) => {
                        const config = STATUS_CONFIG[status];
                        const StatusIcon = config.Icon;
                        const descriptions: Record<RecommendationStatus, string> = {
                          [RecommendationStatus.SEAL_OF_APPROVAL]: "The absolute best. Can't miss.",
                          [RecommendationStatus.RECOMMENDED]: "Solid spot. Worth a visit.",
                          [RecommendationStatus.ITS_ALRIGHT]: "Decent, but nothing special.",
                          [RecommendationStatus.WANT_TO_VISIT]: "On the bucket list.",
                          [RecommendationStatus.DO_NOT_RECOMMEND]: "Save your time (and money).",
                        };

                        const isSealOfApproval = status === RecommendationStatus.SEAL_OF_APPROVAL;

                        return (
                          <tr 
                            key={status} 
                            className={`
                                group transition-colors animate-in fade-in slide-in-from-bottom-2 fill-mode-backwards
                                ${isSealOfApproval ? 'bg-status-seal/10 hover:bg-status-seal/20' : 'hover:bg-brand-cream/50'}
                            `}
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            <td className="p-3 w-12">
                              <StatusIcon size={20} strokeWidth={2.5} className={`mx-auto ${config.textColor}`} />
                            </td>
                            <td className="p-3 font-bold text-brand-dark">
                              <span className={`${config.textColor}`}>
                                {config.label}
                              </span>
                            </td>
                            <td className="p-3 text-brand-dark-light">
                              {descriptions[status]}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            </div>

            <p className="text-brand-dark font-medium italic">
                Stop choosing between a 4.5 star and a 4.6 star place. Good shout.
            </p>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-cream-dark bg-white">
            <button 
                onClick={onClose}
                className="w-full py-3 bg-brand-red text-white rounded-xl font-bold shadow-lg hover:bg-brand-red-hover transition active:scale-95"
            >
                Enough Chit Chat! 👋
            </button>
        </div>

      </div>
    </div>
  );
};
